import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Star, 
  Play, 
  CheckCircle, 
  Shield, 
  Globe,
  Calendar,
  Sparkles,
  Target,
  Crown,
  Briefcase,
  Clock,
  Package2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useServices, usePackages, useCategories, type ServicePackage } from '../hooks/useFirestoreServices';
import LoadingSpinner from '../components/LoadingSpinner';
import PackagesSection from '../components/PackagesSection';
import AnimatedCounter from '../components/AnimatedCounter';
import { generateReviews, getReviewStats } from '../utils/reviewGenerator';

// Import admin setup utilities and expose to window
import adminSetup from '../utils/adminSetup';
import { quickUploadData } from '../utils/quickDataUpload';

// Hero Section Component
function Hero() {
  return (
    <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center text-white">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6"
          >
            <Star className="h-4 w-4 text-yellow-400 mr-2" />
            <span className="text-sm font-medium">Rated 4.9/5 by 5,000+ customers</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold leading-tight mb-6"
          >
            Experience
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              {' '}Doorstep Service{' '}
            </span>
            Like Never Before
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto"
          >
            From beauty treatments to home repairs, get professional services 
            delivered to your doorstep with just a few clicks.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <Link
              to="/services"
              className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center group"
            >
              Start Booking - It's Free!
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/about"
              className="bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 flex items-center justify-center group"
            >
              <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              How RAPSAPP Works
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {[
              { number: '5000+', label: 'Happy Customers', animate: true },
              { number: '1000+', label: 'Service Professionals', animate: true },
              { number: '8+', label: 'Service Categories', animate: true },
              { number: '24/7', label: 'Customer Support', animate: false }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1 md:mb-2">
                  {stat.animate ? (
                    <AnimatedCounter value={stat.number} duration={2000} />
                  ) : (
                    stat.number
                  )}
                </div>
                <div className="text-primary-200 text-xs md:text-base">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// How It Works Section
function HowItWorks() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-primary-900 to-secondary-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-black/20"></div>
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-white opacity-5 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-white opacity-5 rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-white opacity-10 rounded-full"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Briefcase className="h-4 w-4 mr-2" />
            Simple Process
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            How It 
            <span className="block bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Works
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Getting professional services has never been easier. Follow these simple steps.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "01",
              title: "Choose Your Service",
              description: "Browse our extensive catalog of services and select what you need. From beauty to home repairs, we have it all.",
              icon: Target,
              gradient: "from-blue-400 to-cyan-400"
            },
            {
              step: "02", 
              title: "Book & Schedule",
              description: "Pick your preferred date and time. Our verified professionals will be matched to your requirements instantly.",
              icon: Calendar,
              gradient: "from-green-400 to-emerald-400"
            },
            {
              step: "03",
              title: "Enjoy Quality Service",
              description: "Sit back and relax while our professional takes care of everything. Track progress and rate your experience.",
              icon: Star,
              gradient: "from-purple-400 to-pink-400"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="text-center group"
            >
              <div className="relative mb-8">
                <div className={`w-20 h-20 mx-auto bg-gradient-to-r ${item.gradient} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 bg-white text-gray-900 text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">
                  {item.step}
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-gray-300 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/services"
            className="inline-flex items-center bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-primary-600 hover:to-secondary-600 transition-all duration-300 group"
          >
            Start Your Journey
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// E-commerce Style Service Marketplace
function ServiceFinder() {
  const { services, loading } = useServices();
  const { categories } = useCategories();
  const [selectedFilter, setSelectedFilter] = useState('All');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Get categories that should show on homepage
  const homepageCategories = categories.filter(cat => cat.showOnHomepage);
  const homepageCategoryNames = homepageCategories.map(cat => cat.name);
  
  // Filter services that are popular, active, AND belong to homepage categories
  const popularServices = services.filter(service => 
    service.isActive !== false && // Only show active services
    (service.showInPopularSection || service.popular) &&
    (homepageCategoryNames.length === 0 || homepageCategoryNames.includes(service.category))
  );
  
  // Get unique categories for filter buttons
  const filterCategories = ['All', ...homepageCategoryNames];
  
  // Filter services based on selected category
  const filteredServicesList = selectedFilter === 'All' 
    ? popularServices 
    : popularServices.filter(s => s.category === selectedFilter);
  
  // Auto-scroll functionality
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollAmount = 0;
    const scrollStep = 0.5; // Slow and smooth
    const scrollInterval = 20; // Milliseconds

    const autoScroll = setInterval(() => {
      if (isPaused) return;

      if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
        // Reset to beginning
        container.scrollLeft = 0;
        scrollAmount = 0;
      } else {
        scrollAmount += scrollStep;
        container.scrollLeft = scrollAmount;
      }
    }, scrollInterval);

    return () => clearInterval(autoScroll);
  }, [isPaused]);

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Elegant Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-full px-4 py-2 mb-4">
            <Sparkles className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-700">Popular Services</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Our Popular Services
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover our most trusted services with verified professionals and instant booking
          </p>
        </div>

        {/* Improved Category Toggle - Mobile Friendly */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex flex-wrap justify-center gap-2 p-1.5 md:p-1 bg-gray-100 rounded-lg max-w-full">
            {filterCategories.map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`px-4 md:px-5 py-2.5 md:py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  selectedFilter === filter 
                    ? 'bg-white text-gray-900 shadow-sm ring-2 ring-primary-500' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Responsive Grid Cards */}
        <div className="relative">
          {/* Desktop/Tablet: Grid Layout | Mobile: Horizontal Scroll */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {filteredServicesList.slice(0, 4).map((service, index) => {
              const discount = service.discount || (service.originalPrice && service.price 
                ? parseFloat((((service.originalPrice - service.price) / service.originalPrice) * 100).toFixed(1))
                : 0);
              
              return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="group bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  {/* Compact Image Section */}
                  <div className="relative h-44 overflow-hidden bg-gray-50">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Compact Badges */}
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                      <div className="flex flex-col gap-1.5">
                        {service.popular && (
                          <span className="bg-yellow-500 text-white px-2.5 py-0.5 rounded-md text-xs font-bold shadow-md">
                            Popular
                          </span>
                        )}
                        {discount > 0 && (
                          <span className="bg-red-500 text-white px-2.5 py-0.5 rounded-md text-xs font-bold shadow-md">
                            {discount}% OFF
                          </span>
                        )}
                      </div>
                      {/* Category Chip */}
                      <span className="bg-white/95 backdrop-blur-sm text-primary-700 text-xs font-semibold px-2.5 py-1 rounded-md shadow-sm">
                        {service.category}
                      </span>
                    </div>
                  </div>

                  {/* Compact Card Content */}
                  <div className="p-4 flex flex-col flex-grow">
                    {/* Service Name */}
                    <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-primary-600 transition-colors">
                      {service.name}
                    </h3>

                    {/* Compact Stats Row */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                        {(() => {
                          const reviews = generateReviews(service.id);
                          const stats = getReviewStats(reviews);
                          return (
                            <>
                              <span className="font-semibold text-gray-900">{stats.avgRating.toFixed(1)}</span>
                              <span className="text-gray-500">({stats.totalReviews})</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="h-3 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{service.duration}</span>
                      </div>
                    </div>

                    {/* Pricing Section */}
                    <div className="flex items-baseline justify-between mb-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-black text-gray-900">
                          ₹{service.price.toLocaleString()}
                        </span>
                        {service.originalPrice && service.originalPrice > service.price && (
                          <span className="text-xs text-gray-400 line-through">
                            ₹{service.originalPrice.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {service.originalPrice && service.originalPrice > service.price && (
                        <span className="text-xs text-green-600 font-bold">
                          Save ₹{(service.originalPrice - service.price).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Compact CTA Button */}
                    <Link 
                      to={`/services/${service.id}`}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors text-center mt-auto flex items-center justify-center gap-1.5"
                    >
                      Book Now
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )})}
          </div>

          {/* Mobile: Horizontal Scroll */}
          <div 
            ref={scrollContainerRef}
            className="flex md:hidden gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <style>{`
              .flex::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            
            {filteredServicesList.map((service, index) => {
              const discount = service.discount || (service.originalPrice && service.price 
                ? parseFloat((((service.originalPrice - service.price) / service.originalPrice) * 100).toFixed(1))
                : 0);
              
              return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex-none w-[300px] snap-start"
              >
                <div className="group bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  {/* Compact Image Section */}
                  <div className="relative h-44 overflow-hidden bg-gray-50">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Compact Badges */}
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                      <div className="flex flex-col gap-1.5">
                        {service.popular && (
                          <span className="bg-yellow-500 text-white px-2.5 py-0.5 rounded-md text-xs font-bold shadow-md">
                            Popular
                          </span>
                        )}
                        {discount > 0 && (
                          <span className="bg-red-500 text-white px-2.5 py-0.5 rounded-md text-xs font-bold shadow-md">
                            {discount}% OFF
                          </span>
                        )}
                      </div>
                      {/* Category Chip */}
                      <span className="bg-white/95 backdrop-blur-sm text-primary-700 text-xs font-semibold px-2.5 py-1 rounded-md shadow-sm">
                        {service.category}
                      </span>
                    </div>
                  </div>

                  {/* Compact Card Content */}
                  <div className="p-4 flex flex-col flex-grow">
                    {/* Service Name */}
                    <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-primary-600 transition-colors">
                      {service.name}
                    </h3>

                    {/* Compact Stats Row */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                        {(() => {
                          const reviews = generateReviews(service.id);
                          const stats = getReviewStats(reviews);
                          return (
                            <>
                              <span className="font-semibold text-gray-900">{stats.avgRating.toFixed(1)}</span>
                              <span className="text-gray-500">({stats.totalReviews})</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="h-3 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{service.duration}</span>
                      </div>
                    </div>

                    {/* Pricing Section */}
                    <div className="flex items-baseline justify-between mb-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-black text-gray-900">
                          ₹{service.price.toLocaleString()}
                        </span>
                        {service.originalPrice && service.originalPrice > service.price && (
                          <span className="text-xs text-gray-400 line-through">
                            ₹{service.originalPrice.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {service.originalPrice && service.originalPrice > service.price && (
                        <span className="text-xs text-green-600 font-bold">
                          Save ₹{(service.originalPrice - service.price).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Compact CTA Button */}
                    <Link 
                      to={`/services/${service.id}`}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors text-center mt-auto flex items-center justify-center gap-1.5"
                    >
                      Book Now
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )})}
          </div>

          {/* View All Button */}
          <div className="text-center mt-8">
            <Link
              to="/services"
              className="inline-flex items-center bg-gray-900 hover:bg-black text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg group"
            >
              View All Services
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Advertisement Section Component
function AdvertisementSection() {
  return (
    <section className="py-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-r from-pink-400/20 to-red-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-gradient-to-r from-cyan-400/20 to-teal-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            {/* Badge */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="h-4 w-4 text-yellow-300 mr-2" />
              <span className="text-sm font-semibold">Professional Home Services</span>
            </div>

            {/* Main Heading */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Your Home,
              <span className="block bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
                Our Priority
              </span>
            </h2>

            {/* Description */}
            <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-xl">
              Book trusted professionals for home services in minutes. Quality guaranteed, every time.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Browse Services
                </motion.button>
              </Link>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold py-4 px-8 rounded-xl hover:bg-white/20 transition-all duration-300"
                >
                  Learn More
                </motion.button>
              </Link>
            </div>

            {/* Trust Indicators - Simplified */}
            <div className="flex flex-wrap items-center gap-6 text-white/90 text-sm">
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5 text-yellow-400 fill-current" />
                <span className="font-semibold">4.9/5 Rating</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>5,000+ Happy Customers</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-blue-400" />
                <span>100% Verified Pros</span>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Service Benefits Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Benefit Card 1 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-3">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Verified Professionals</h4>
                <p className="text-white/70 text-sm">Background-checked experts you can trust</p>
              </motion.div>

              {/* Benefit Card 2 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Secure Payment</h4>
                <p className="text-white/70 text-sm">Safe transactions with money-back guarantee</p>
              </motion.div>

              {/* Benefit Card 3 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-3">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Same-Day Service</h4>
                <p className="text-white/70 text-sm">Fast booking and quick service delivery</p>
              </motion.div>

              {/* Benefit Card 4 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mb-3">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">24/7 Support</h4>
                <p className="text-white/70 text-sm">Round-the-clock customer assistance</p>
              </motion.div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ y: [-10, 10, -10], rotate: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-pink-400 to-red-500 rounded-full opacity-60 blur-xl"
            ></motion.div>
            <motion.div
              animate={{ y: [10, -10, 10], rotate: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full opacity-50 blur-xl"
            ></motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Auto-scrolling Testimonials Carousel Component
function TestimonialsCarousel({ testimonials }: { testimonials: any[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Infinite auto scroll every 5 seconds for smoother experience
  useEffect(() => {
    if (testimonials.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // Infinite scroll - always move to next index
        return (prevIndex + 1) % testimonials.length;
      });
    }, 5000); // Changed to 5 seconds for smoother transitions

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Calculate which testimonials to show for infinite scroll (1 on mobile, 2 on desktop)
  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    
    const visible = [];
    // Check if mobile view (window width < 768px)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const count = isMobile ? 1 : 2;
    
    // Create seamless infinite scroll by showing 1 or 2 consecutive testimonials
    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push({ 
        ...testimonials[index], 
        displayIndex: index,
        uniqueKey: `infinite-${currentIndex}-${i}-${Date.now()}`, // More unique key for infinite scroll
        position: i // Track position for animation
      });
    }
    return visible;
  };

  const visibleTestimonials = getVisibleTestimonials();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-16"
    >
      {/* Testimonials Container */}
      <div className="relative">
        {/* Scroll Indicators */}
        <div className="flex justify-center mb-6">
          <div className="flex space-x-1">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 ${
                  currentIndex === index
                    ? 'w-8 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full'
                    : 'w-3 h-3 bg-slate-300 hover:bg-slate-400 rounded-full'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 min-h-[400px]">
          {visibleTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.uniqueKey}
              initial={{ x: 100 }}
              animate={{ x: 0 }}
              exit={{ x: -100 }}
              transition={{ 
                duration: 0.7,
                delay: index * 0.15,
                ease: [0.25, 0.1, 0.25, 1], // Smooth slider easing
                type: "tween"
              }}
              className="group"
            >
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 border border-slate-200/50 shadow-sm h-full">
                {/* User Info */}
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 shadow-lg"
                  />
                  <div className="ml-4">
                    <h4 className="font-bold text-slate-900 text-lg">{testimonial.name}</h4>
                    <p className="text-slate-600">{testimonial.role}</p>
                    <p className="text-slate-500 text-sm">{testimonial.location}</p>
                  </div>
                  <div className="ml-auto">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 px-3 py-1 rounded-full">
                      <span className="text-sm font-semibold text-green-700">{testimonial.highlight}</span>
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className="h-5 w-5 text-yellow-400 fill-current"
                    />
                  ))}
                  <span className="ml-2 text-slate-700 font-medium">Perfect Experience</span>
                </div>

                {/* Testimonial */}
                <blockquote className="text-slate-700 mb-6 leading-relaxed italic">
                  "{testimonial.story}"
                </blockquote>

                {/* Service & Results */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="text-sm text-slate-500 mb-1">Service Used</div>
                    <div className="font-semibold text-slate-900">{testimonial.service}</div>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                    <div className="text-sm text-green-700 mb-1">Transformation</div>
                    <div className="text-xs text-slate-600">{testimonial.beforeAfter.before}</div>
                    <div className="text-xs text-green-600 font-medium">→ {testimonial.beforeAfter.after}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// Consolidated Customer Trust and Testimonials Section  
function CustomerTrustAndTestimonials() {
  // Combine the best testimonials from all sections
  const featuredTestimonials = [
    {
      name: "Sapna Sharma",
      role: "Homeowner",
      location: "Mandi, Himachal Pradesh",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face",
      story: "RAPSAPP ne meri ghar ki safai ka experience hi badal diya! Professional time pe aaye, verified the, aur kaam bhi bahut accha kiya. Itni reliable service pehle kabhi nahi mili!",
      service: "Deep House Cleaning",
      rating: 5,
      beforeAfter: { before: "2+ hours searching", after: "5 minutes booking" },
      highlight: "Saved 120+ minutes"
    },
    {
      name: "Rahul Thakur",
      role: "Business Owner", 
      location: "Bilaspur, Himachal Pradesh",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face",
      story: "Mere office ke liye perfect electrician minutes mein mil gaya! Service quality aur professionalism ekdum top class tha. Business turant resume ho gaya, koi tension nahi!",
      service: "Electrical Repair",
      rating: 5,
      beforeAfter: { before: "3 days waiting", after: "Same day service" },
      highlight: "Business saved"
    },
    {
      name: "Anjali Chauhan",
      role: "Working Mom",
      location: "Kangra, Himachal Pradesh",
      image: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop&crop=face",
      story: "Working mom hone ke naate, RAPSAPP ek lifesaver hai! Quick booking, reliable professionals, aur har baar excellent service. Jaise ek personal assistant ho!",
      service: "Beauty Services",
      rating: 5,
      beforeAfter: { before: "Stressful scheduling", after: "Effortless booking" },
      highlight: "Stress-free life"
    },
    {
      name: "Ankush Kumar",
      role: "Senior Citizen",
      location: "Hamirpur, Himachal Pradesh",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face",
      story: "Meri umar mein bharosemand help chahiye hoti hai. RAPSAPP ke verified professionals se peace of mind milti hai. App use karna bhi easy hai aur customer service ekdum excellent!",
      service: "Home Maintenance",
      rating: 5,
      beforeAfter: { before: "Unsafe workers", after: "Verified professionals" },
      highlight: "Peace of mind"
    },
    {
      name: "Vikram Parmar",
      role: "Hotel Owner",
      location: "Kullu, Himachal Pradesh",
      image: "https://images.unsplash.com/photo-1557862921-37829c790f19?w=100&h=100&fit=crop&crop=face",
      story: "Tourist season mein last minute help chahiye thi. RAPSAPP ne same day service provide kari! Ab har emergency ke liye yahi use karte hain. Best service provider platform!",
      service: "Plumbing Services",
      rating: 5,
      beforeAfter: { before: "Last minute chaos", after: "Instant solution" },
      highlight: "Emergency hero"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/40 to-purple-100/40 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-pink-100/40 to-rose-100/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-gradient-to-br from-cyan-100/40 to-teal-100/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-200/50 rounded-full px-6 py-3 mb-6">
            <Crown className="h-5 w-5 text-yellow-500 mr-2" />
            <span className="text-sm font-semibold text-slate-700">Trusted by Thousands</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-slate-900">
            Why 5,000+ Customers
            <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Trust RAPSAPP
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Real stories from real customers who transformed their service experience with RAPSAPP
          </p>
        </motion.div>

        {/* Customer Testimonials - Horizontal Auto Scroll */}
        <TestimonialsCarousel testimonials={featuredTestimonials} />

      </div>
    </section>
  );
}

// Main HomePage Component
export default function HomePage() {
  console.log('🏠 [HomePage] Component rendering...');
  
  // Make admin setup and quick upload available in console
  useEffect(() => {
    console.log('🏠 [HomePage] Component mounted successfully!');
    if (typeof window !== 'undefined') {
      (window as any).adminSetup = adminSetup;
      (window as any).quickUploadData = quickUploadData;
      console.log('🔧 Utilities loaded. Use these commands in console:');
      console.log('   🚀 await quickUploadData() - Upload all services & categories to Firestore');
      console.log('   await checkAdminUsers() - Check existing admin users');
      console.log('   await createAdminUser(userId, email, name) - Create admin user');
      console.log('   await migrateUserToAdmin(userId) - Migrate existing user to admin');
    }
  }, []);

  // Track website visits
  useEffect(() => {
    const trackVisit = () => {
      const visits = localStorage.getItem('websiteVisits');
      const newVisitCount = visits ? parseInt(visits) + 1 : 1;
      localStorage.setItem('websiteVisits', newVisitCount.toString());
      
      // Track unique daily visits
      const today = new Date().toDateString();
      const lastVisit = localStorage.getItem('lastVisitDate');
      
      if (lastVisit !== today) {
        localStorage.setItem('lastVisitDate', today);
        // This is a new daily visit
      }
    };
    
    trackVisit();
  }, []);

  return (
    <>
      <Hero />
      {/* Packages Section - Replaced Premium Services */}
      <PackagesSection />
      <AdvertisementSection />
      <ServiceFinder />
      <HowItWorks />
      <CustomerTrustAndTestimonials />
    </>
  );
}
